import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Skeleton } from './ui/skeleton';
import { AlertCircle } from 'lucide-react';

/**
 * ProtectedRoute - Requires authentication
 * Optionally requires specific role (member, leader, admin)
 * In preview mode, real admins/DGs can access any role's routes.
 */
export const ProtectedRoute = ({
  children,
  requiredRole = 'member',
  redirectTo = '/login'
}) => {
  const { isAuthenticated, hasRole, loading, isRealAdmin, previewMode } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pcc-background">
        <div className="space-y-4 w-full max-w-md px-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // In preview mode, real admins can access any route
  if (previewMode && isRealAdmin()) {
    return children;
  }

  if (!hasRole(requiredRole)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

/**
 * VisitorBlockedMessage — Shown when a visitor tries to access member-only features
 */
const VisitorBlockedMessage = () => {
  const { isDark } = useTheme();
  return (
    <div className={`min-h-screen flex items-center justify-center p-6 ${isDark ? 'bg-[#272727]' : 'bg-pcc-background'}`}>
      <div className={`max-w-sm w-full rounded-2xl p-8 text-center ${isDark ? 'bg-[#333333] border border-[#444444]' : 'bg-white border border-gray-100'}`}>
        <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center ${isDark ? 'bg-amber-900/30' : 'bg-amber-50'}`}>
          <AlertCircle size={32} className="text-amber-500" />
        </div>
        <h2 className={`text-lg font-bold mb-2 ${isDark ? 'text-[#F2F1EB]' : 'text-gray-900'}`}>
          Accès réservé aux membres
        </h2>
        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Cette fonctionnalité est réservée aux membres de l'église. Contactez l'équipe pastorale pour plus d'informations.
        </p>
      </div>
    </div>
  );
};

/**
 * MemberRoute - Requires member role or higher
 * Authenticated visitors see a "reserved for members" message instead of a redirect.
 */
export const MemberRoute = ({ children }) => {
  const { isAuthenticated, hasRole, loading, user, isRealAdmin, previewMode } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pcc-background">
        <div className="space-y-4 w-full max-w-md px-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // In preview mode, real admins can access any route
  if (previewMode && isRealAdmin()) {
    return children;
  }

  // Visitor accessing member routes → show blocked message
  if (user?.role === 'visitor' && !hasRole('member')) {
    return <VisitorBlockedMessage />;
  }

  if (!hasRole('member')) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

/**
 * AuthenticatedRoute - Requires authentication only (any role including visitor)
 * Used for routes like /member/profile that all authenticated users can access.
 */
export const AuthenticatedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-pcc-background">
        <div className="space-y-4 w-full max-w-md px-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-8 w-3/4" />
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

/**
 * LeaderRoute - Requires leader role or higher
 */
export const LeaderRoute = ({ children }) => {
  return (
    <ProtectedRoute requiredRole="leader">
      {children}
    </ProtectedRoute>
  );
};

/**
 * AdminRoute - Requires admin role
 */
export const AdminRoute = ({ children }) => {
  return (
    <ProtectedRoute requiredRole="admin">
      {children}
    </ProtectedRoute>
  );
};

export default ProtectedRoute;
